require('./passwordPositions.scss');

// @ vendor
const React = require('react');
const PropTypes = require('prop-types');
const { connect } = require('react-redux');
const PureRenderMixin = require('react-addons-pure-render-mixin');
const classNames = require('classnames');
const Immutable = require('immutable');

// @ actions
const passwordPositionsActions = require('actions/contactCenter/passwordPositions');

// @ constants
const statusValues = require('constants/contactCenter/passwordPositionsStatusValues');

// @ utilities
const { FormattedText, formatText } = require('core/i18n').i18n;
const { createDigitsEnumeration } = require('utilities/signaturePatternHelper');

// @ components
const { OKButton } = require('components/commons/OKButton/OKButton');
const BlockInfo = require('components/contactCenter/blockInfo/blockInfo');
const SignaturePasscode = require('components/signaturePattern/signaturePatternCodeInput/signaturePasscode/signaturePasscode');
const HorizontalLoader = require('components/contactCenter/horizontalLoader/horizontalLoader');

class PasswordPositions extends React.Component {
    constructor(props) {
        super(props);
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
        this.handleOnInputDigit = this.handleOnInputDigit.bind(this);
        this.handleReloadConfigClick = this.handleReloadConfigClick.bind(this);
        this.handlePasswordRecovery = this.handlePasswordRecovery.bind(this);
        this.handleOnEnter = this.handleOnEnter.bind(this);
        this.state = {
            immDigitsValues: Immutable.List(),
            digitsChanged: false
        };
    }

    componentDidMount() {
        const { processRequestConfiguration, documentType, documentNumber } = this.props;

        if (documentType && documentNumber) {
            processRequestConfiguration(documentType, documentNumber);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.shouldRequestConfiguration(nextProps)) {
            this.props.processRequestConfiguration(nextProps.documentType, nextProps.documentNumber);
        }

        if (this.shouldResetComponent(nextProps)) {
            this.props.reset();
            this.setState({
                immDigitsValues: Immutable.List(),
                digitsChanged: false
            });
        }

        if (this.shouldInitializeDigitsValues(nextProps)) {
            const numberOfRequiredDigits = nextProps.immPasswordPositions.get('requiredDigits').size;

            this.setState({
                immDigitsValues: Immutable.fromJS(new Array(numberOfRequiredDigits).fill('')),
                digitsChanged: false
            });
        }
    }

    componentWillUnmount() {
        this.props.reset();
    }

    shouldRequestConfiguration(nextProps) {
        return nextProps.documentType
            && nextProps.documentNumber
            && (nextProps.documentType !== this.props.documentType
                || nextProps.documentNumber !== this.props.documentNumber);
    }

    shouldResetComponent(nextProps) {
        return (!nextProps.documentType || !nextProps.documentNumber);
    }

    shouldInitializeDigitsValues(nextProps) {
        return nextProps.immPasswordPositions !== this.props.immPasswordPositions
            && nextProps.immPasswordPositions.get('status') === statusValues.CONFIG_FETCHED_SUCCESSFULLY
            && !this.state.digitsChanged;
    }

    handleOnInputDigit(newDigit, position) {
        const { onInputDigit, immPasswordPositions } = this.props;

        const positions = immPasswordPositions.get('requiredDigits').toJS();
        const newDigits = this.state.immDigitsValues.set(position, newDigit);
        const values = newDigits.toJS();

        // We cannot update the state first and use the new value immediately later because setState is asynchronous!
        onInputDigit({ positions, values });

        this.setState({
            digitsChanged: true,
            immDigitsValues: newDigits
        });
    }

    handleReloadConfigClick() {
        const { processRequestConfiguration, documentType, documentNumber } = this.props;

        processRequestConfiguration(documentType, documentNumber);
    }

    handlePasswordRecovery() {
        const { passwordRecoveryCallback } = this.props;

        if (passwordRecoveryCallback) {
            passwordRecoveryCallback();
        }
    }

    handleOnEnter(position) {
        const { onEnterKey } = this.props;

        if (onEnterKey) {
            onEnterKey(position);
        }
    }

    buildIdleState(passwordPositionsClassNames) {
        const numberOfPositionsAsString = `-- ${formatText('passwordPositions-positions')}`;

        return (
            <div className={passwordPositionsClassNames}>
                <BlockInfo extraClass="password-positions__script-block">
                    <FormattedText
                        value="passwordPositions-confirmationScript"
                        injectedStrings={[ numberOfPositionsAsString, '--' ]}
                    />
                </BlockInfo>
            </div>
        );
    }

    buildFetchingState(passwordPositionsClassNames) {
        const numberOfPositionsAsString = `-- ${formatText('passwordPositions-positions')}`;

        return (
            <div className={passwordPositionsClassNames}>
                <BlockInfo extraClass="password-positions__script-block">
                    <FormattedText
                        value="passwordPositions-confirmationScript"
                        injectedStrings={[ numberOfPositionsAsString, '--' ]}
                    />
                </BlockInfo>
                <div className="password-positions__code-input-section">
                    <div className="password-positions__code-input-title">
                        <FormattedText value="passwordPositions-codeInputTitle" />
                    </div>
                    <div className="password-positions__loading-section">
                        <HorizontalLoader />
                    </div>
                </div>
            </div>
        );
    }

    buildFetchedSuccessfullyState(passwordPositionsClassNames) {
        const { immPasswordPositions, isInvalidPasswordError, isPasswordBlockedError, disabled } = this.props;
        const numberOfRequiredDigits = immPasswordPositions.get('requiredDigits').size;
        const numberOfPositionsAsString = `${numberOfRequiredDigits} ${numberOfRequiredDigits == 1 ? formatText('passwordPositions-position') : formatText('passwordPositions-positions')}`;
        const requiredDigits = immPasswordPositions.get('requiredDigits').toJS();
        const immRequiredDigits = immPasswordPositions.get('requiredDigits').map(digit => digit - 1);
        const immDigitsValues = this.state.immDigitsValues;
        const keySize = immPasswordPositions.get('passwordLength');
        const errorMessage = this.buildErrorMessage();

        return (
            <div className={passwordPositionsClassNames}>
                <BlockInfo extraClass="password-positions__script-block">
                    <FormattedText
                        value="passwordPositions-confirmationScript"
                        injectedStrings={[ numberOfPositionsAsString, createDigitsEnumeration(requiredDigits) ]}
                    />
                </BlockInfo>
                <div className="password-positions__code-input-section">
                    <div className="password-positions__code-input-title">
                        <FormattedText value="passwordPositions-codeInputTitle" />
                    </div>
                    <SignaturePasscode
                        disabled={disabled}
                        error={isInvalidPasswordError || isPasswordBlockedError}
                        keySize={keySize}
                        numbersOnly={true}
                        className="password-positions__code-input"
                        immRequiredDigits={immRequiredDigits}
                        immDigitsValues={immDigitsValues}
                        onChange={this.handleOnInputDigit}
                        onKeyDown={this.handleOnInputDigit}
                        onEnter={this.handleOnEnter}
                        clearOnError
                    />
                    {errorMessage}
                </div>
            </div>
        );
    }

    buildErrorMessage() {
        const { isInvalidPasswordError, isPasswordBlockedError, passwordRecoveryCallback } = this.props;

        const recoveryPasswordLink = !passwordRecoveryCallback ? null :
        (
            <p onClick={this.handlePasswordRecovery} className="password-positions__password-recovery">
                {formatText("passwordPositions-passwordRecovery")}
            </p>
        );

        if (isInvalidPasswordError) {
            return (
                <div className="password-positions__input-error-section">
                    <p className="password-positions__invalid-input-message">
                        <FormattedText value="passwordPositions-invalidInputMessage" />
                    </p>
                    <p className="password-positions__try-again-message">
                        <FormattedText value="passwordPositions-retryInputMessage" />
                    </p>
                </div>
            );
        }

        if (isPasswordBlockedError) {
            return (
                <div className="password-positions__input-error-section">
                    <p className="password-positions__blocked-user-message">
                        <FormattedText value="passwordPositions-blockedUserMessage" />
                    </p>
                    {recoveryPasswordLink}
                </div>
            );
        }

        return null;
    }

    buildFetchErrorState(passwordPositionsClassNames) {
        const numberOfPositionsAsString = `-- ${formatText('passwordPositions-positions')}`;

        return (
            <div className={passwordPositionsClassNames}>
                <BlockInfo extraClass="password-positions__script-block">
                    <FormattedText
                        value="passwordPositions-confirmationScript"
                        injectedStrings={[ numberOfPositionsAsString, '--' ]}
                    />
                </BlockInfo>
                <div className="password-positions__code-input-section">
                    <div className="password-positions__code-input-title">
                        <FormattedText value="passwordPositions-codeInputTitle" />
                    </div>
                    <div className="password-positions__config-fetch-error-section">
                        <FormattedText value="passwordPositions-configFetchError" />&nbsp;
                        <OKButton
                            modifier="additional-action-primary"
                            onClick={this.handleReloadConfigClick}
                            id="btn_signaturePattern_resend_error"
                        >
                            <FormattedText value="passwordPositions-refetchConfig" />
                        </OKButton>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { className, immPasswordPositions } = this.props;
        const passwordPositionsClassNames = classNames('password-positions', className);

        switch(immPasswordPositions.get('status')) {
            case statusValues.FETCHING_CONFIG:
                return this.buildFetchingState(passwordPositionsClassNames);

            case statusValues.CONFIG_FETCHED_SUCCESSFULLY:
                return this.buildFetchedSuccessfullyState(passwordPositionsClassNames);

            case statusValues.CONFIG_FETCH_ERROR:
                return this.buildFetchErrorState(passwordPositionsClassNames);

            default:
                return this.buildIdleState(passwordPositionsClassNames);
        }
    }
}

PasswordPositions.propTypes = {
    // API
    documentType: PropTypes.string,
    documentNumber: PropTypes.string,
    className: PropTypes.string,
    onInputDigit: PropTypes.func.isRequired,
    onEnterKey: PropTypes.func,
    disabled: PropTypes.bool,
    isInvalidPasswordError: PropTypes.bool,
    isPasswordBlockedError: PropTypes.bool,
    passwordRecoveryCallback: PropTypes.func,

    // Private props
    immPasswordPositions: PropTypes.object.isRequired,
    processRequestConfiguration: PropTypes.func,
    reset: PropTypes.func
}

module.exports.constructor = PasswordPositions;
module.exports.PasswordPositions = connect(
    state => ({
        immPasswordPositions: state.passwordPositions
    }),
    {
        processRequestConfiguration: passwordPositionsActions.processRequestConfiguration,
        reset: passwordPositionsActions.reset
    }
)(PasswordPositions);
